// The one place that formats a timeline timestamp. The clock used to be written out inline in both
// globalTimelineFeed.js and storySteps.js (the same `toLocaleTimeString` call, copy-pasted), which
// is how the two drifted from the design.
//
// Figma's "date and time-timeline" component (40007495:315886) shows the two-line variant: the date
// over a 12-hour clock — "23 Aug" / "10:24 am" — and every timeline surface (the global feed, the
// story rail, the pack mini-rows) now draws that same pair, whatever the item's age: there is no
// "today,"/"yesterday," wording on the rail (Pouria, 2026-08-18 — a rail mixing "16 Aug" with
// "today,15:31" is the drift the design doesn't have). `hour12` stays a flag, not a second
// implementation, in case a non-rail surface ever needs the 24-hour form again.

import { parseUtc } from '@/lib/dueStatus'

// `new Date(null)` is the unix EPOCH, not an invalid date, so an undated value would otherwise be
// stamped "1 Jan / 12:00 am" — a perfectly convincing timestamp for something that has none. Same
// guard, and same reason, as `dueStatus`/`dueAmount`: the RAW value has to be checked, before it
// ever reaches Date.
const noDate = (v) => v === null || v === undefined || v === ''

// "10:24 am" (Figma) or "10:24" (the story steps' 24-hour form). Built by hand rather than through
// toLocaleTimeString so the output is identical in every locale — the design specifies one shape.
export function clockTime(date, { hour12 = true } = {}) {
  if (noDate(date)) return ''
  const dt = date instanceof Date ? date : parseUtc(date)
  if (!dt || isNaN(dt.getTime())) return ''
  const minutes = String(dt.getMinutes()).padStart(2, '0')
  const hours = dt.getHours()
  if (!hour12) return `${String(hours).padStart(2, '0')}:${minutes}`
  return `${hours % 12 || 12}:${minutes} ${hours >= 12 ? 'pm' : 'am'}`
}

// "23 Aug" — day first, as the design draws it, whatever order the locale would have picked, and the
// month abbreviated to three letters (Pouria on #25: the full "August" crowds the 10px stamp).
// Pinned to en-US rather than the ambient locale for the same reason `clockTime` is built by hand:
// the design specifies ONE shape, and "short" elsewhere is neither three letters nor English.
export function dayAndMonth(date) {
  if (noDate(date)) return ''
  const dt = date instanceof Date ? date : parseUtc(date)
  if (!dt || isNaN(dt.getTime())) return ''
  try { return `${dt.getDate()} ${dt.toLocaleDateString('en-US', { month: 'short' })}` } catch { return '' }
}

// The compact clock the SCHEDULE line uses: "8pm", "10pm", "8:30am". The frame
// (40006440:182480) writes a whole hour with no minutes at all — ":00" is noise on a 10px detail
// line — while the stamp on the right keeps the full "10:24 am" form above.
export function shortClock(date) {
  if (noDate(date)) return ''
  const dt = date instanceof Date ? date : parseUtc(date)
  if (!dt || isNaN(dt.getTime())) return ''
  const hours = dt.getHours()
  const suffix = hours >= 12 ? 'pm' : 'am'
  const minutes = dt.getMinutes()
  return `${hours % 12 || 12}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}${suffix}`
}

// WHEN the thing is scheduled, as the row's calendar detail line reads it: "Today, 8pm",
// "Tomorrow, 8am", and a plain "23 Jul, 8am" for anything further out. Relative to the same ticking
// `now` the statuses use, so a row rolls from "Today" to "Yesterday" without a reload.
// Yesterday is named too: an overdue row that just says "8pm" hides WHICH 8pm it missed.
export function scheduleDay(value, now = Date.now()) {
  const days = dayDistance(value, now)
  if (days === null) return ''
  const dt = value instanceof Date ? value : parseUtc(value)
  return days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : days === -1 ? 'Yesterday' : dayAndMonth(dt)
}

// HOW MANY CALENDAR DAYS away — 0 today, 1 tomorrow, -1 yesterday — or null for anything undated.
//
// Compared as calendar DAYS in the viewer's own zone, never as a 24-hour distance: 11pm and 1am are
// two hours apart and belong to different days, and every near-midnight bug this rail has had came
// from measuring the gap in milliseconds instead. Exported because the event lifecycle
// (lib/eventTime.js, busy-my-web#239) needs the NUMBER — "In 3 days" — where `scheduleDay` needs
// only the word, and two files each rounding their own midnights is how the two would drift.
// The LAST INSTANT of the calendar day a value falls in, in the viewer's own zone — the answer to
// "when is a whole-day thing over?" (busy-my-web#239's all-day bounce). Built from the START of the
// next day rather than by adding 23:59:59.999 to a midnight, the same way lib/dateBounds.js decides
// whether a day is behind a floor: a day is not always 24 hours long, and the two DST days a year
// are exactly the ones a hand-rolled "+1 day" gets wrong. Null for anything undated.
export function dayEnd(value) {
  if (noDate(value)) return null
  const dt = value instanceof Date ? value : parseUtc(value)
  if (!dt || isNaN(dt.getTime())) return null
  return new Date(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() + 1).getTime() - 1)
}

export function dayDistance(value, now = Date.now()) {
  if (noDate(value)) return null
  const dt = value instanceof Date ? value : parseUtc(value)
  if (!dt || isNaN(dt.getTime())) return null
  const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  return Math.round((midnight(dt) - midnight(new Date(now))) / (24 * 60 * 60 * 1000))
}

// The whole line: the day and the clock. An all-day row passes `hour12: false`-style nothing — it
// calls `scheduleDay` directly and adds its own "All day", since it has no clock to print.
export function scheduleText(value, now = Date.now()) {
  const day = scheduleDay(value, now)
  if (!day) return ''
  const clock = shortClock(value)
  return clock ? `${day}, ${clock}` : day
}

// The two lines of the Figma component for one instant. Callers keep them apart because they render
// stacked and right-aligned, each on its own line.
export function timelineDateTime(value) {
  if (noDate(value)) return { date: '', time: '' }
  const dt = value instanceof Date ? value : parseUtc(value)
  if (!dt || isNaN(dt.getTime())) return { date: '', time: '' }
  return { date: dayAndMonth(dt), time: clockTime(dt) }
}
